// Pure derivation of the Coverage view's shapes from the outline payload, the requirement list and
// the section<->requirement junction. Deliberately free of React and of any store access: it lives
// beside the slice because selectors.js builds coverage, and having that import reach into layout/
// made the dependency direction layout -> slices -> layout.
//
// Everything downstream keys on a requirement's refLabel, so this is where that namespace is
// established — once, from the authoritative source, rather than trusted to line up.
//
// `sectionRequirementIds` (section id -> requirement ids) comes from the section<->requirement
// junction, so it is built from database keys and cannot mis-resolve. When it is present the required
// points are DERIVED from the requirements themselves, which makes `point.id === requirement.refLabel`
// true by construction — the invariant this whole view rests on, and the one the generator used to
// break by inventing its own id scheme ("rp5" for what was really R15).
//
// When it is absent (a legacy outline, or a response predating the field) the generator's own
// requiredPoints and coveredPointIds are used exactly as before. That path can still mis-resolve; it
// is a fallback so an old outline renders at all, not a supported state.

// Shared empties, so a document of paragraphs that match nothing does not allocate one array per row.
const NO_MATCHES = [];
const NO_VALUES = [];

export const buildCoverage = (
  outlineData,
  requirements = [],
  sectionRequirementIds = null
) => {
  const sections = outlineData?.sections || [];
  const hasLinks = sectionRequirementIds != null;

  const refLabelsBySection = hasLinks
    ? refLabelsFromLinks(sections, requirements, sectionRequirementIds)
    : refLabelsFromCoveredPointIds(sections);

  // With links, the denominator is every active requirement — including the ones no section
  // addresses, which is exactly what makes them countable as gaps. The generator's requiredPoints
  // list is a lossy subset of them (it merges and drops), so using it would hide real gaps.
  const requiredPoints = hasLinks
    ? requirements.map((requirement) => ({
        id: requirement.refLabel,
        text: requirement.restatedText,
        origin: requirement.sectionRef,
        category: (requirement.categories || [])[0] || null,
      }))
    : outlineData?.requiredPoints || [];

  const coveredIds = new Set();
  // refLabel -> titles of the sections that address it (order = section order).
  // Empty array means the requirement is a gap (no addressing section).
  const coveringByPoint = {};
  requiredPoints.forEach((point) => {
    coveringByPoint[point.id] = [];
  });

  sections.forEach((section) => {
    (refLabelsBySection[section.id] || []).forEach((refLabel) => {
      coveredIds.add(refLabel);
      if (coveringByPoint[refLabel]) {
        coveringByPoint[refLabel].push(section.title);
      }
    });
  });

  const gapIds = new Set();
  requiredPoints.forEach((point) => {
    if (!coveredIds.has(point.id)) {
      gapIds.add(point.id);
    }
  });

  return {
    requiredPoints,
    sections,
    coveringByPoint,
    refLabelsBySection,
    hasLinks,
    gapIds: Array.from(gapIds),
    coveredCount: requiredPoints.filter((p) => !gapIds.has(p.id)).length,
    totalCount: requiredPoints.length,
  };
};

// Junction ids resolved to refLabels. A link to a requirement that is no longer active (curated away
// since the links were built) resolves to nothing and is dropped rather than left as a dangling id.
const refLabelsFromLinks = (sections, requirements, sectionRequirementIds) => {
  const refLabelById = new Map(
    (requirements || []).map((requirement) => [
      requirement.id,
      requirement.refLabel,
    ])
  );
  const bySection = {};
  sections.forEach((section) => {
    const requirementIds = sectionRequirementIds[section.id] || [];
    bySection[section.id] = requirementIds
      .map((requirementId) => refLabelById.get(requirementId))
      .filter(Boolean);
  });
  return bySection;
};

const refLabelsFromCoveredPointIds = (sections) => {
  const bySection = {};
  sections.forEach((section) => {
    bySection[section.id] = section.coveredPointIds || [];
  });
  return bySection;
};

// refLabel -> requirement. Three call sites used to build this same map independently, one of them
// on every row click; derive it once (selectRequirementByRefLabel) and pass it in.
export const buildRequirementByRefLabel = (requirements) =>
  new Map(
    (requirements || [])
      .filter((requirement) => requirement?.refLabel)
      .map((requirement) => [requirement.refLabel, requirement])
  );

// Returns the required points visible in the checklist for the current trace
// state: all of them when nothing is traced, otherwise only those linked to
// the traced section (possibly none).
export const getVisibleRequiredPoints = (coverage, activeSection) => {
  if (!activeSection) {
    return coverage.requiredPoints;
  }
  const coveredIds = new Set(refLabelsOf(coverage, activeSection));
  return coverage.requiredPoints.filter((point) => coveredIds.has(point.id));
};

// The refLabels a section addresses. Always read through this rather than off
// `section.coveredPointIds`: on the linked path those raw strings are not the namespace in use, and
// reading them directly is what silently produced empty results before.
//
// Note the two levels of absence. When the coverage object HAS a refLabelsBySection map, it is
// authoritative and a section missing from it genuinely addresses nothing — falling back to
// coveredPointIds there would resurrect the very strings that failed to resolve. Only when there is no
// map at all (a coverage object assembled without one) do we read the section's own ids.
export const refLabelsOf = (coverage, section) => {
  if (!section) {
    return [];
  }
  if (coverage?.refLabelsBySection) {
    return coverage.refLabelsBySection[section.id] || [];
  }
  return section.coveredPointIds || [];
};

// The sections list is flat with a `level` per row, so a row's children are the
// rows that follow it until the level stops being deeper — the same rule the
// outline table's expand model uses. `ancestorIds` carries every enclosing row
// so a collapsed parent can hide a whole subtree without re-walking the list.
export const buildSectionRows = (coverage) => {
  const sections = coverage?.sections || [];
  const stack = [];
  return sections.map((section, index) => {
    const level = section.level || 1;
    while (stack.length && stack[stack.length - 1].level >= level) {
      stack.pop();
    }
    const ancestorIds = stack.map((entry) => entry.id);
    stack.push({ id: section.id, level });

    const next = sections[index + 1];
    return {
      id: section.id,
      title: section.title,
      level,
      ancestorIds,
      hasChildren: (next?.level || 0) > level,
      // Counted from the resolved refLabels, not from coveredPointIds. Those disagreed whenever the
      // ids failed to resolve, which is how a section could advertise "9 requirements" while the
      // table below it could link none of them.
      requirementCount: refLabelsOf(coverage, section).length,
    };
  });
};

// The sections addressing a row's requirements, in outline order, resolved to section ids. Never by
// title: coveringByPoint carries titles and two sections can legitimately share one, so a row click
// resolved that way could land on the wrong section.
export const sectionIdsForRow = (coverage, row) => {
  const pointIds = new Set(row?.pointIds || []);
  if (pointIds.size === 0) {
    return [];
  }
  return (coverage?.sections || [])
    .filter((section) =>
      refLabelsOf(coverage, section).some((refLabel) => pointIds.has(refLabel))
    )
    .map((section) => section.id);
};

export const filterVisibleSectionRows = (rows, collapsedIds) => {
  if (!collapsedIds?.length) {
    return rows;
  }
  const collapsed = new Set(collapsedIds);
  return rows.filter(
    (row) => !row.ancestorIds.some((ancestorId) => collapsed.has(ancestorId))
  );
};

// The docx viewer disambiguates a quote that appears more than once by comparing
// a 0–1 "distance" — parseDocXFile stores paragraphIndex / totalParagraphs — so
// handing it an absolute paragraph index always selects the LAST occurrence. The
// viewer does not expose the document's true paragraph count, so approximate it
// with the furthest paragraph any requirement of the same document reaches, which
// is how ShreddingReport derives its own denominator (selectLastParagraphIndex).
// Returns 0 when there is nothing to go on, which favours the first match.
export const paragraphDistanceFor = (requirement, requirements) => {
  const start = requirement?.paragraphIndexStart;
  if (start == null) {
    return 0;
  }
  const furthest = (requirements || []).reduce((max, other) => {
    if (other?.sourceDocumentId !== requirement.sourceDocumentId) {
      return max;
    }
    return Math.max(
      max,
      other.paragraphIndexEnd ?? other.paragraphIndexStart ?? 0
    );
  }, 0);

  return furthest > 0 ? Math.min(1, start / furthest) : 0;
};

// One row per paragraph of ONE source document — the full-document view, so a reader can see the
// parts no requirement was extracted from as well as the parts that were. A paragraph carries the
// requirements whose provenance range covers its index, which is where its refs, categories and
// addressing sections come from; a paragraph with none simply has empty ones.
//
// documentId is required to be correct, not merely nice: every document numbers its paragraphs from
// zero, so without filtering on sourceDocumentId a requirement extracted from one document lands on
// whichever paragraph happens to share its index in the document being listed.
//
// The refs, categories, addressing sections and known-ness of a requirement are the same on every
// paragraph of its range, so they are derived once per requirement rather than re-merged per row: a
// requirement spanning N paragraphs used to allocate three Sets per row to recompute them.
export const buildParagraphRows = ({
  paragraphs,
  requirements,
  coverage,
  documentId = null,
}) => {
  const coveringByPoint = coverage?.coveringByPoint || {};

  // paragraphIndexEnd is inclusive and may be absent, in which case the requirement covers the
  // single paragraph it started on.
  const entriesByParagraph = new Map();
  (requirements || []).forEach((requirement) => {
    const start = requirement?.paragraphIndexStart;
    if (start == null) {
      return;
    }
    if (documentId != null && requirement.sourceDocumentId !== documentId) {
      return;
    }
    const { refLabel } = requirement;
    const covering = coveringByPoint[refLabel];
    const entry = {
      requirement,
      // The requirement's own refLabel, not a positional label derived from the required-point list.
      // That derivation used to number rows by their index in requiredPoints, so a row showing "R5"
      // could be requirement R15 — a mislabel that read as correct.
      refs: refLabel ? [refLabel] : NO_VALUES,
      refLabel,
      categories: [...new Set(requirement.categories || [])],
      addressedIn: covering || NO_VALUES,
      // Distinguishes "no section addresses this requirement" from "this refLabel is not in the
      // coverage namespace at all". The second is a data fault, and reporting it as a gap made every
      // requirement in the document look unaddressed.
      isKnown: covering !== undefined,
    };

    const end = requirement.paragraphIndexEnd ?? start;
    for (let index = start; index <= end; index += 1) {
      const existing = entriesByParagraph.get(index) || [];
      existing.push(entry);
      entriesByParagraph.set(index, existing);
    }
  });

  // Use native `pIndex` because {paragraph.index} is white-space inclusive;
  // paragraphs[] returns all non-empty Paragraphs -> `pIndex` is more accurate.
  return (paragraphs || []).map((paragraph, pIndex) => {
    const matched = entriesByParagraph.get(pIndex) || NO_MATCHES;
    const only = matched.length === 1 ? matched[0] : null;

    // refs and pointIds are the same list — a refLabel that is absent could never match a trace step
    // or an addressing section anyway, so the filtered one serves both.
    const refs = only
      ? only.refs
      : matched.map((entry) => entry.refLabel).filter(Boolean);
    const categories = only
      ? only.categories
      : [...new Set(matched.flatMap((entry) => entry.categories))];
    const addressedIn = only
      ? only.addressedIn
      : [...new Set(matched.flatMap((entry) => entry.addressedIn))];

    return {
      id: `p${pIndex}`,
      paragraphIndex: pIndex,
      refs,
      pointIds: refs,
      text: paragraph.text,
      restatement: only ? only.requirement.restatedText : null,
      page: paragraph.pageNumber ?? null,
      addressedIn,
      categories,
      // A paragraph nothing was extracted from is not a "gap" — a gap is a requirement no
      // section addresses. Only a paragraph that did yield a KNOWN requirement can be one.
      isGap:
        matched.length > 0 &&
        matched.some((entry) => entry.isKnown) &&
        addressedIn.length === 0,
      isHeading: (paragraph.level || 0) > 0,
      requirement: matched[0]?.requirement ?? null,
    };
  });
};

// The source documents the Coverage view can list paragraphs for, in outline order, each with the
// label its picker shows. Only documents with a nextgen id are listed — the paragraph endpoint is
// keyed by that id, so a row without one has nothing to fetch.
//
// The file name is the label because docTypeLabel alone is ambiguous: the classifier routinely gives
// several documents the same type. When names still collide (the same file linked twice, or a
// document whose nextgen row has no name at all) the type and then the id are appended, because an
// option you cannot tell apart is worse than a long one.
const qualifiedName = ({ name, typeLabel }) =>
  typeLabel && typeLabel !== name ? `${name} — ${typeLabel}` : name;

export const buildDocumentOptions = (documents = []) => {
  const candidates = (documents || [])
    .filter((document) => document?.nextgenDocumentId !== null)
    .map((document) => ({
      id: document.nextgenDocumentId,
      name:
        document.fileName ||
        document.docTypeLabel ||
        `Document ${document.nextgenDocumentId}`,
      typeLabel: document.docTypeLabel || null,
      isPrimary: Boolean(document.isPrimary),
    }));

  const countByName = {};
  candidates.forEach((candidate) => {
    countByName[candidate.name] = (countByName[candidate.name] || 0) + 1;
  });
  const countByQualified = {};
  candidates.forEach((candidate) => {
    const qualified = qualifiedName(candidate);
    countByQualified[qualified] = (countByQualified[qualified] || 0) + 1;
  });

  return candidates.map((candidate) => {
    if (countByName[candidate.name] === 1) {
      return { ...candidate, label: candidate.name };
    }
    const qualified = qualifiedName(candidate);
    return {
      ...candidate,
      label:
        countByQualified[qualified] === 1
          ? qualified
          : `${qualified} (#${candidate.id})`,
    };
  });
};

// One row per required point: the point supplies the text, the requirement of the same refLabel
// supplies page and source-document provenance. `point.id === requirement.refLabel` holds by
// construction on the linked path (buildCoverage derives the points from the requirements) rather
// than by assumption, which is what it used to be.
export const buildCoverageRows = ({
  coverage,
  requirements,
  activeSection,
  requirementByRefLabel = null,
}) => {
  const byRefLabel =
    requirementByRefLabel || buildRequirementByRefLabel(requirements);
  const gapIds = new Set(coverage.gapIds);

  return getVisibleRequiredPoints(coverage, activeSection).map((point) => {
    const requirement = byRefLabel.get(point.id);
    // Show the document's own words (verbatimQuote) — that is also what gets
    // highlighted in the source pane, so the row and the document read alike. The
    // point's text is the model's restatement; it becomes the hover instead, and
    // only where it differs and there is a verbatim quote to prefer over it.
    const verbatim = requirement?.verbatimQuote || null;
    return {
      id: point.id,
      refs: [point.id],
      pointIds: [point.id],
      paragraphIndex: requirement?.paragraphIndexStart ?? null,
      text: verbatim || point.text,
      restatement: verbatim && point.text !== verbatim ? point.text : null,
      page: requirement?.pageNumber ?? null,
      addressedIn: coverage.coveringByPoint[point.id] || [],
      categories: requirement?.categories || [],
      isGap: gapIds.has(point.id),
      requirement: requirement || null,
    };
  });
};

// The paragraphs a section traces to, in the order the reader sees them numbered (R1, R2, …), which
// is requiredPoints order and not the order the section happens to list its coveredPointIds in. A
// point whose requirement has no paragraph provenance is not a step: there is nowhere to scroll to,
// so counting it would promise a destination that does not exist.
export const buildSectionTraceSteps = ({
  activeSection,
  coverage,
  requirements,
  requirementByRefLabel = null,
}) => {
  if (!activeSection) {
    return [];
  }
  const covered = new Set(refLabelsOf(coverage, activeSection));
  const byRefLabel =
    requirementByRefLabel || buildRequirementByRefLabel(requirements);

  const seenParagraphKeys = new Set();
  return (
    (coverage.requiredPoints || [])
      .filter((point) => covered.has(point.id))
      .map((point) => {
        const requirement = byRefLabel.get(point.id);
        if (requirement?.paragraphIndexStart == null) {
          return null;
        }
        return {
          pointId: point.id,
          documentId: requirement.sourceDocumentId ?? null,
          paragraphIndex: requirement.paragraphIndexStart,
        };
      })
      .filter(Boolean)
      // Two required points can resolve to the same paragraph of the same document — N counts
      // reachable paragraphs, not points, so keep only the first (lowest R number, since
      // requiredPoints is already in that order) and drop the rest.
      .filter((step) => {
        const key = `${step.documentId}:${step.paragraphIndex}`;
        if (seenParagraphKeys.has(key)) {
          return false;
        }
        seenParagraphKeys.add(key);
        return true;
      })
  );
};

// Lookups the view needs on every render — by row id, and by the point a trace step names. Built
// once per row list so a 4000-paragraph document is not re-scanned on hovers and pane toggles that
// cannot have changed it. A point maps to the FIRST row carrying it, matching the findIndex this
// replaced.
export const buildRowIndex = (rows) => {
  const rowsById = new Map();
  const rowIndexById = new Map();
  const rowIndexByPointId = new Map();
  (rows || []).forEach((row, index) => {
    rowsById.set(row.id, row);
    rowIndexById.set(row.id, index);
    (row.pointIds || []).forEach((pointId) => {
      if (!rowIndexByPointId.has(pointId)) {
        rowIndexByPointId.set(pointId, index);
      }
    });
  });
  return { rowsById, rowIndexById, rowIndexByPointId };
};

// The two counts the summary line states, in one traversal rather than one filter each.
export const countRowSummary = (rows) => {
  let withRequirementsCount = 0;
  let gapCount = 0;
  (rows || []).forEach((row) => {
    if ((row.refs || []).length > 0) {
      withRequirementsCount += 1;
    }
    if (row.isGap) {
      gapCount += 1;
    }
  });
  return { withRequirementsCount, gapCount };
};

// Where a step lands in the list currently on screen. -1 means the step belongs to a document whose
// paragraphs are not the ones being listed. Pass rowIndexByPointId (from buildRowIndex) to resolve
// it as a lookup instead of a scan.
export const findRowForStep = ({ rows, step, rowIndexByPointId = null }) => {
  if (!step) {
    return -1;
  }
  if (rowIndexByPointId) {
    return rowIndexByPointId.get(step.pointId) ?? -1;
  }
  return (rows || []).findIndex((row) =>
    (row.pointIds || []).includes(step.pointId)
  );
};
